import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { X, ZoomIn } from 'lucide-react';
import { Link } from 'react-router-dom';

const ArtworkModal = ({ artwork, onClose }) => {
  const { t, language } = useLanguage();
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold">{artwork.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image with Magnifying Glass Effect */}
            <div className="relative">
              <div 
                className="relative bg-gray-100 rounded-lg overflow-hidden cursor-zoom-in"
                onMouseEnter={() => setIsZoomed(true)}
                onMouseLeave={() => setIsZoomed(false)}
                onMouseMove={handleMouseMove}
              >
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="w-full h-auto"
                  style={{
                    transform: isZoomed ? 'scale(2)' : 'scale(1)',
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    transition: isZoomed ? 'none' : 'transform 0.3s ease'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1536924940846-227afb31e2a5?w=800&h=800&fit=crop';
                  }}
                />
                {!isZoomed && (
                  <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-2 rounded-md flex items-center space-x-2">
                    <ZoomIn className="w-4 h-4" />
                    <span className="text-sm">{language === 'en' ? 'Hover to zoom' : 'Survoler pour zoomer'}</span>
                  </div>
                )}
              </div>

              {/* Availability Badge */}
              <div className="mt-4">
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                  artwork.available
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {artwork.available ? t.gallery.available : t.gallery.sold}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold mb-4">{t.gallery.details.description}</h3>
                <p className="text-gray-700 leading-relaxed">
                  {language === 'en' ? artwork.description.en : artwork.description.fr}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold text-gray-700">{t.gallery.details.year}</span>
                  <span className="text-gray-900">{artwork.year}</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold text-gray-700">{t.gallery.details.size}</span>
                  <span className="text-gray-900">{artwork.size}</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold text-gray-700">{t.gallery.details.medium}</span>
                  <span className="text-gray-900">{artwork.medium}</span>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold text-gray-700">{t.gallery.details.colors}</span>
                  <div className="flex items-center space-x-2">
                    {artwork.colors.map((color, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-700">
                        {color}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="font-semibold text-gray-700">{t.gallery.details.price}</span>
                  <span className="text-xl font-bold text-gray-900">{artwork.price} €</span>
                </div>
              </div>

              {/* Inquiry Button */}
              {artwork.available && (
                <Link
                  to="/contact"
                  onClick={onClose}
                  className="block w-full text-center px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-medium"
                >
                  {t.gallery.inquire}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkModal;
